/**
 * Reads the NDJSON stream of `POST /api/agent/speech` when called with
 * `{ text, conversationId }` (a piece of a still-streaming answer): each
 * line is `{"type":"audio","pcm":"<base64 16-bit PCM>","sampleRate":number}`
 * until a trailing `{"type":"usage", token, costUsd, promptTokens,
 * audioTokens}` (always last) or an `{"type":"error","error":string}`.
 * Mirrors `agentStream.ts`'s line-buffering shape for the chat NDJSON — a
 * small, duplicated contract on this side, no shared lib between backend
 * and frontend. Pure: takes a byte reader shaped like
 * `ReadableStreamDefaultReader`'s `read()`, so it is unit-testable without a
 * real `Response`/`ReadableStream`.
 *
 * Also home to the two pure wire-format conversions the audio player needs:
 * base64 → bytes, and 16-bit PCM bytes → the floats Web Audio wants
 * (`pcm16ToFloat32`, ported unchanged from the MEIS-13490 prototype's
 * `agentSpeechOutput.ts`, which kept it inline — split out here so it can be
 * unit-tested without a browser audio stack).
 */

export type AgentSpeechUsage = {
  /** Opaque; booked through the GraphQL mutation `bookAgentSpeechUsage` once the answer's messageId is known. */
  token: string;
  costUsd: number;
  promptTokens: number;
  audioTokens: number;
};

export type AgentSpeechStreamEvent =
  | { type: "audio"; pcm: string; sampleRate: number }
  | { type: "error"; error: string }
  | ({ type: "usage" } & AgentSpeechUsage);

export type AgentSpeechByteReader = {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
};

export class AgentSpeechStreamError extends Error {
  /** The terminal usage line that followed an `error` line (the partial cost of the failed piece), when one arrived. */
  readonly usage?: AgentSpeechUsage;

  constructor(message: string, usage?: AgentSpeechUsage) {
    super(message);
    this.usage = usage;
  }
}

/**
 * Reads the stream line by line, buffering a partial trailing line across
 * chunks. Every complete line is one `AgentSpeechStreamEvent`; the `usage`
 * line is terminal and is not handed to `onEvent` — it resolves this call.
 * An `error` line rejects with the server's reason — after the stream is
 * drained to its end, so the `usage` line the server still sends (the
 * partial cost) travels on the error; a stream that ends without either is
 * an error too.
 */
export async function readAgentSpeechStream(
  reader: AgentSpeechByteReader,
  onEvent: (event: { type: "audio"; pcm: string; sampleRate: number }) => void
): Promise<AgentSpeechUsage> {
  const decoder = new TextDecoder();
  let buffer = "";
  let usage: AgentSpeechUsage | null = null;
  let failure: string | null = null;

  const handleLine = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as AgentSpeechStreamEvent;
    if (event.type === "error") {
      failure ??= event.error;
      return;
    }
    if (event.type === "usage") {
      const { type: _type, ...rest } = event;
      usage = rest;
      return;
    }
    onEvent(event);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    lines.forEach(handleLine);
  }
  // Flush a multi-byte character the last chunk left half-decoded.
  buffer += decoder.decode();
  handleLine(buffer);

  if (failure !== null)
    throw new AgentSpeechStreamError(failure, usage ?? undefined);
  if (!usage)
    throw new AgentSpeechStreamError(
      "The connection ended before the speech usage line arrived."
    );
  return usage;
}

/** Decodes a base64 string (as sent for an `audio` event's `pcm`) to its raw bytes. */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Little-endian 16-bit samples as floats in [-1, 1). A piece may end in the
 * middle of a sample: that byte comes back as `carry` for the next piece.
 */
export function pcm16ToFloat32(
  bytes: Uint8Array,
  carry: number | null
): { samples: Float32Array; carry: number | null } {
  const data =
    carry === null ? bytes : Uint8Array.from([carry, ...Array.from(bytes)]);
  const count = Math.floor(data.length / 2);
  const samples = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const value = data[2 * i] | (data[2 * i + 1] << 8);
    samples[i] = (value >= 0x8000 ? value - 0x10000 : value) / 0x8000;
  }
  return {
    samples,
    carry: data.length % 2 === 1 ? data[data.length - 1] : null,
  };
}
